<template>
  <div class="flex-column gap-18">
    <div>
      <template v-if="loginTitle && loginDescription">
        <h3 class="m-0 mb-2">{{ $t(loginTitle) }}</h3>
        <p class="mb-2">{{ $t(loginDescription) }}</p>
      </template>
      <p v-else class="bold has-text-size-14 mb-1">
        {{ $t(loginInputLabel) }}
      </p>
      <PhoneInput v-model="phoneLogin.customerPhone" @send="submitPhone" />
    </div>
    <button class="btn btnL mt-1 p-2 has-text-size-14" @click="submitPhone">
      {{ $t(loginButtonToken) }}
    </button>
  </div>
  <div v-if="isOTPPopupOpen" class="otp-backdrop">
    <div class="otp-popup">
      <template v-if="phoneLogin.phoneLoginStatus === 1">
        <div class="flex-column gap-10">
          <div class="flex-row align-items-center gap-18">
            <div class="illustration">
              <IllustrationsShield />
            </div>
            <p class="has-text-size-20 bold">
              {{ $t("bnpl:checkCode") }}
            </p>
          </div>
          <p>
            {{ $t("bnpl:typeCheckCode") }}
          </p>
        </div>
      </template>

      <template v-else-if="phoneLogin.phoneLoginStatus === 2">
        <div class="flex-column gap-10">
          <div class="flex-row align-items-center gap-18">
            <div style="width: 39px; height: 39px">
              <IllustrationsErrorCross />
            </div>
            <div class="tryAgain">
              {{ $t("bnpl:login.fail") }}
            </div>
          </div>
          <p>{{ $t("bnpl:tryAgain") }}</p>
        </div>
      </template>

      <div>
        <InputOtp
          v-model="phoneLogin.checkCodeDigits"
          :length="6"
          class="flex-row checkCodeDigitsContainer"
          @input="inputOtp($event)"
          @paste="pasteOtp($event)"
        >
          <template #default="{ attrs, events, index }">
            <input
              type="text"
              inputmode="numeric"
              v-bind="attrs"
              v-on="events"
              class="textInput checkCodeInputField"
              autofocus
              autocomplete="one-time-code"
            />
          </template>
        </InputOtp>
      </div>

      <button id="submitCode" ref="submitCheckCodeButton" class="btn btnL stretch grow" @click="submitCheckCode">
        {{ $t("bnpl:login") }}
      </button>
      <div class="flex-column align-items-center stretch gap32">
        <div>
          <button class="linkButton linkButtonSuccess" :disabled="phoneLogin.seconds > 0" @click="resendCheckCode">
            <i class="pi pi-refresh" />{{ $t("bnpl:resendCode") }}
          </button>
        </div>
        <div v-if="phoneLogin.seconds > 0">
          <p class="p-body bold" style="color: var(--systemWarning)">
            <strong>{{ phoneLogin.seconds }}</strong>
            {{ $t("bnpl:canSendNewCodeAfterSec") }}
          </p>
        </div>
      </div>
      <div class="close" @click="closePopup">
        <IconsClose />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useSessionStore } from "~/stores/session";
import { generateNonce } from "~/utils/util";
import { useUserStore } from "~/stores/user";
import { useRoute } from "vue-router";
import { useSelfcareStore } from "~/stores/selfcare";
import { BNPL_APPLICATION_STATUS } from "~/utils/const";
import { useOnboardingStore } from "~/stores/onboarding";
import { loadInternalMessages } from "~/utils/i18n";
import { i18nInstance } from "~/plugins/i18n";
import { useProfileStore } from "~/stores/profile";
import { usePrescoreStore } from "~~/stores/prescore";
import { useCartStore } from "~~/stores/cart";
import { useCountryStore } from "~~/stores/country";
import { ref } from "vue";

const route = useRoute();

const props = defineProps({
  loginTitle: {
    type: String,
    required: false,
  },
  loginDescription: {
    type: String,
    required: false,
  },
  loginInputLabel: {
    type: String,
    default: "bnpl:phone"
  },
  loginButtonToken: {
    type: String,
    default: "bnpl:payment.login.button.start",
  },
});

const sessionStore = useSessionStore();
const userStore = useUserStore();
const bnplApplicationStore = useBnplApplicationStore();
const selfcareStore = useSelfcareStore();
const onboardingStore = useOnboardingStore();
const profileStore = useProfileStore();
const prescoreStore = usePrescoreStore();
const cartStore = useCartStore();
const countryStore = useCountryStore();

let secondFactorId = null;

const errorText = ref(null);

const showErrorToast = ref(false);

const submitCheckCodeButton = ref(null);

const isCheckCodeSubmitted = ref(false);

const isOTPPopupOpen = ref(false);

const countDown = ref(null);

const otpCode = ref("");
const inputOtp = async (e) => {
  e.preventDefault();
  if(e.target.value.length === 1) {
    otpCode.value = otpCode.value.concat(e.target.value);
  }
  if(e.target.value.length === 2) { //idiot safari sends OTP autofill in this format (123456): 1, 12, 13, 14, 15, 16
    otpCode.value = otpCode.value.concat(e.target.value.charAt(1));
  }
  if(otpCode.value.length === 6) {
    phoneLogin.checkCodeDigits = otpCode.value;
    otpCode.value = "";
  }
  await validateAndSubmitCheckCode();
};
const pasteOtp = async (e) => {
  e.preventDefault();
  let paste = e.clipboardData.getData('text');
  if(paste && paste.length === 6) {
    phoneLogin.checkCodeDigits = paste;
  }
  await validateAndSubmitCheckCode();
};

const phoneLogin = reactive({
  customerPhone: userStore.customerPhone,
  phoneLoginStatus: 0,
  checkCodeDigits: null,
  seconds: 30,
});

const closePopup = () => {
  isOTPPopupOpen.value = !isOTPPopupOpen.value;
};

function popupErrorToast() {
  showErrorToast.value = true;
  setTimeout(() => {
    showErrorToast.value = false;
  }, 3000);
}

function submitPhone() {
  let nonce = generateNonce();
  sessionStore.nonce = nonce;
  let loginRequest = {
    user: phoneLogin.customerPhone,
    nonce: nonce,
    bnpl: {
      publicId: route.params.publicId,
      financier: route.params.financer,
    },
  };
  
  if (route.name === "financer-prescore") {
    if (!cartStore.cart.sum) {
      popupErrorToast();
      return;
    }
  }
  sessionStore.login(loginRequest).then(
    async (result) => {
      if (!!result.bypass) {
        if (route.name === "financer-publicId") {
          sessionStore.savedLocalPosition = result.flowPosition;
          await loadInternalMessages(i18nInstance, countryStore.locale);
          bnplApplicationStore.loadEverything(route).then(
            () => {
              if (result.status === BNPL_APPLICATION_STATUS.INITIAL && result.flowPosition !== "INTRO") {
                onboardingStore.load(countryStore.countryId).then(() => {
                  bnplApplicationStore.bnplApplication.currentPosition = "ONBOARDING";
                });
              } else if (result.status === BNPL_APPLICATION_STATUS.CANCELLED) {
                window.open("/" + route.params.financer, "_self");
              } else {
                bnplApplicationStore.bnplApplication.currentPosition = result.flowPosition;
              }
            },
            () => {
              console.log("Hiba a betöltés közben");
            }
            );
          } else if (route.name === "financer") {
            await loadInternalMessages(i18nInstance, countryStore.locale);
            await profileStore.fetch();
            selfcareStore.navigateTo("dashboard");
          }
      } else {
        secondFactorId = result.secondFactorResponse.secondFactorId;
      }
      phoneLogin.phoneLoginStatus = 1;
      Track.event("login");
      isOTPPopupOpen.value = true;
    },
    (error) => {
      errorText.value = "Hibás vagy nem létező telefonszám";
      console.log("Nem létező vagy rossz telefonszám hiba " + error);
    }
    );
    
    clearInterval(countDown.value);
    phoneLogin.seconds = 30;
    createCountDown();
  }

async function validateAndSubmitCheckCode() {
  const otpInputValid = /^\d{6}$/.test(phoneLogin.checkCodeDigits);
  if(otpInputValid){
    await submitCheckCode();
  }
}
async function submitCheckCode() {
  if (isCheckCodeSubmitted.value) {
    return;
  }
  
  let fullCheckCode = phoneLogin.checkCodeDigits.slice(0, 6);
  let finishLoginRequest = {
    nonce: sessionStore.nonce,
    code: fullCheckCode,
    id: secondFactorId,
  };
  isCheckCodeSubmitted.value = true;
  
  try {
    await sessionStore.finishLogin(finishLoginRequest).then(
      async (result) => {
        Track.event("login_2fa");
        fbq('trackCustom', 'IcBnplLogin');
        if (route.name === "financer-publicId") {
          sessionStore.savedLocalPosition = result.flowPosition;
          await loadInternalMessages(i18nInstance, countryStore.locale);
          bnplApplicationStore.loadEverything(route).then(
            () => {
              if (result.status === BNPL_APPLICATION_STATUS.INITIAL && result.flowPosition !== "INTRO") {
                onboardingStore.load(countryStore.countryId).then(() => {
                  bnplApplicationStore.bnplApplication.currentPosition = "ONBOARDING";
                });
              } else if (result.status === BNPL_APPLICATION_STATUS.CANCELLED) {
                window.open("/" + route.params.financer, "_self");
              } else {
                bnplApplicationStore.bnplApplication.currentPosition = result.flowPosition;
              }
            },
            () => {
              console.log("Hiba a betöltés közben");
            }
            );
          } else if (route.name === "financer") {
            await loadInternalMessages(i18nInstance, countryStore.locale);
            await profileStore.fetch();
            selfcareStore.navigateTo("dashboard");
          } else if (route.name === "financer-prescore") {
            await prescoreStore.createPrescore();
          await bnplApplicationStore.loadEverything(route);
          await profileStore.fetch();
          navigateTo({
            name: "financer-prescore-prescoreId",
            params: { prescoreId: bnplApplicationStore.publicId },
          });
        }
      },
      function (error) {
        phoneLogin.phoneLoginStatus = 2;
        phoneLogin.checkCodeDigits = null;
        otpCode.value = "";
        isCheckCodeSubmitted.value = false;
        console.log("HIBA" + error);
      }
    );
  } catch (error) {
    phoneLogin.phoneLoginStatus = 2;
    phoneLogin.checkCodeDigits = null;
    otpCode.value = "";
    isCheckCodeSubmitted.value = false;
    console.log("HIBA" + error);
  }
  otpCode.value = "";
  phoneLogin.checkCodeDigits = null;
}

function resendCheckCode() {
  submitPhone();
  phoneLogin.seconds = 30;
  clearInterval(countDown.value);
  createCountDown();
}

function createCountDown() {
  countDown.value = setInterval(() => {
    phoneLogin.seconds--;
    if (phoneLogin.seconds === 0) {
      clearInterval(countDown.value);
    }
  }, 1000);
}
</script>

<style scoped lang="scss">
.otp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.4);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.otp-popup {
  position: relative;
  background: white;
  padding: 32px 24px;
  margin: 8px;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  gap: 20px;
  width: min-content;
}

.illustration {
  svg {
    width: 64px;
    height: 64px;
  }
}

@media only screen and (max-width: 792px) {
  .otp-popup {
    padding: 24px;
  }
}

.checkCodeInputField {
  width: 48px;
  height: 48px;
  text-align: center;
}

.inputFieldsContainer {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}

.checkCodeDigitsContainer {
  gap: 16px;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;

  svg {
    display: block;
    margin: 0;
  }
}

@media only screen and (max-width: 792px) {
  .inputFieldsContainer {
    display: grid;
    grid-gap: 12px;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    align-items: center;
  }

  .checkCodeInputField {
    width: 42px;
    height: 42px;
  }

  .checkCodeDigitsContainer {
    gap: 8px;
  }
}
</style>
